import { createFileRoute } from "@tanstack/react-router";
import { pageHead, serviceSchema, breadcrumbSchema, jsonLdScript } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

const PAGE_PATH = "/leistungen/geo-generative-engine-optimization";

const HERO_DESCRIPTION =
  "Generative Engine Optimization sorgt dafür, dass Sie zu den Quellen gehören, die ChatGPT, Perplexity und Googles AI Overviews in ihren Antworten nennen — nicht überall, sondern dort, wo die Nennung wirtschaftlich zählt.";

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Was ist Generative Engine Optimization (GEO)?",
    a: "GEO ist die systematische Optimierung von Inhalten und digitaler Präsenz dafür, dass KI-Systeme wie ChatGPT, Perplexity und Googles AI Overviews eine Marke als Quelle zitieren oder empfehlen. Anders als klassisches SEO zielt GEO nicht auf einen Ranking-Platz, sondern auf die Nennung in der synthetisierten Antwort.",
  },
  {
    q: "Worin unterscheidet sich GEO von klassischem SEO?",
    a: "SEO bringt Sie in die Trefferliste, GEO in die KI-Antwort. SEO optimiert für Rankings, GEO für die Auswahl als zitierte Quelle. Beide teilen die technische Basis — saubere Struktur, strukturierte Daten, Autorität — werden aber unterschiedlich gemessen.",
  },
  {
    q: "Kann man eine Platzierung in KI-Antworten garantieren?",
    a: "Nein. Welche Quellen ein KI-System nennt, wechselt von Prompt zu Prompt und von Monat zu Monat. Seriöse Arbeit erhöht die Wahrscheinlichkeit einer Nennung und macht sie messbar — garantieren lässt sie sich nicht.",
  },
  {
    q: "Für welche Unternehmen lohnt sich GEO?",
    a: "Vor allem dort, wo Zielgruppen Kauf- oder Beauftragungsentscheidungen über KI-Assistenten vorbereiten — typischerweise erklärungsbedürftige B2B-Leistungen, E-Commerce und Anbieter mit Wettbewerb um informationsgetriebene Anfragen. Wo die Zielgruppe nicht über KI recherchiert, ist GEO kein sinnvolles Investment.",
  },
  {
    q: "Wie misst man GEO-Erfolg?",
    a: "Über den Share of Answer: den Anteil, mit dem eine Marke zu definierten, geschäftsrelevanten Fragen in KI-Antworten genannt wird, im Vergleich zum Wettbewerb und über die Zeit. Nicht über klassische Rankings oder Klickzahlen.",
  },
  {
    q: "Ersetzt GEO das klassische SEO?",
    a: "Nein. GEO baut auf SEO auf. Ohne saubere technische Basis, strukturierte Daten und Autorität ist nachhaltige KI-Sichtbarkeit nicht möglich. Die beiden Disziplinen wirken zusammen, nicht gegeneinander.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const steps = [
  {
    num: "01",
    title: "Bestandsaufnahme: Wo nennt KI Sie, wo den Wettbewerb?",
    body: "Wir messen den Ausgangspunkt. Zu welchen geschäftsrelevanten Fragen erscheint Ihre Marke heute in ChatGPT, Perplexity, Gemini und Googles AI Overviews — und mit welchem Anteil im Vergleich zum Wettbewerb. Erst dieser Share-of-Answer-Baseline zeigt, wo Sie stehen und wo die Lücken liegen.",
  },
  {
    num: "02",
    title: "Wirtschaftliche Priorisierung statt Gießkanne",
    body: "Nicht jede KI-Sichtbarkeit ist wertvoll. Wir priorisieren die Frage-Cluster nach geschäftlicher Relevanz: Wo bereiten Nutzer Kauf- oder Beauftragungsentscheidungen vor, wo ist die KI-Antwort der Moment, in dem die Auswahl fällt. Themen ohne wirtschaftliche Wirkung lassen wir bewusst aus.",
  },
  {
    num: "03",
    title: "Entitäten, Struktur und Zitierfähigkeit",
    body: "KI-Systeme zitieren Quellen, die sie eindeutig verstehen und einordnen können. Wir schärfen Ihre Entität — konsistente Benennung über alle Kanäle, strukturierte Daten nach Schema.org, überprüfbare Fakten, Autoren- und Quellennachweise — und bauen Inhalte so, dass sie als klar abgrenzbare Antwort-Bausteine extrahierbar sind. Dazu die technische Seite: Crawlbarkeit für KI-Bots, saubere Auszeichnung, llms.txt.",
  },
  {
    num: "04",
    title: "Messung und Iteration, ohne Garantieversprechen",
    body: "KI-Antworten verändern sich laufend. Wir verfolgen Ihren Share-of-Answer über die Zeit, halten Inhalte aktuell und justieren nach. Was wir berichten, ist Ihr realer Anteil an den relevanten Antworten und dessen Entwicklung — nicht eine Platzierung, die wir ohnehin nicht garantieren könnten.",
  },
];

const capabilities = [
  { name: "Share-of-Answer-Messung", detail: "ChatGPT, Perplexity, Gemini, AI Overviews" },
  { name: "Entitäten-Modellierung", detail: "konsistente Marken- und Themen-Entitäten" },
  { name: "Strukturierte Daten", detail: "Schema.org, FAQ, überprüfbare Fakten" },
  { name: "Antwort- & Snippet-Optimierung", detail: "extrahierbare Inhalts-Bausteine" },
  { name: "Technische KI-Crawlbarkeit", detail: "Bot-Zugang, llms.txt, saubere Auszeichnung" },
  { name: "E-E-A-T-Signale", detail: "Autorenprofile, Quellen, nachweisbare Expertise" },
  { name: "Digitale PR & Drittnennungen", detail: "Sichtbarkeit im offenen Web" },
  { name: "Verzahnung mit SEO", detail: "gemeinsame technische Basis" },
];

const stack = [
  { name: "Google Search Console", small: "Indexierung, Crawlbarkeit" },
  { name: "Sistrix", small: "Sichtbarkeit, Wettbewerb" },
  { name: "Screaming Frog", small: "Technische Audits, strukturierte Daten" },
];

export const Route = createFileRoute("/leistungen/geo-generative-engine-optimization")({
  head: () => {
    const h = pageHead("geoGenerativeEngine", "de");
    const meta = [...h.meta, { name: "robots", content: "index, follow, max-image-preview:large" }];
    const scripts = [
      jsonLdScript(
        serviceSchema({
          name: "Generative Engine Optimization (GEO)",
          description: HERO_DESCRIPTION,
          url: PAGE_PATH,
          serviceType: "Generative Engine Optimization",
        }),
      ),
      jsonLdScript(
        breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "GEO — Generative Engine Optimization", path: PAGE_PATH },
        ]),
      ),
      jsonLdScript(faqJsonLd),
    ];
    return { meta, links: h.links, scripts };
  },
  component: GeoPage,
});

function GeoPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="GEO — Generative Engine Optimization"
          headline={
            <>
              GEO ist <em>keine Hype-Disziplin</em> — sondern die Frage, ob KI-Systeme Sie als Quelle nennen.
            </>
          }
          lead="Klassische Rankings beantworten nur noch die halbe Frage. Immer mehr Kauf- und Anbieter-Recherchen enden in ChatGPT, Perplexity und Googles AI Overviews — in einer synthetisierten Antwort, die wenige Quellen nennt und alles andere weglässt. Generative Engine Optimization sorgt dafür, dass Sie zu diesen Quellen gehören. Nicht überall, sondern dort, wo die Nennung wirtschaftlich zählt."
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "GEO — Generative Engine Optimization" },
          ]}
        />

        <Section
          number="02"
          kicker="Standpunkt"
          headline={
            <>
              Gefunden werden und empfohlen werden sind <em>zwei verschiedene Dinge.</em>
            </>
          }
        >
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">
                Klassisches SEO bringt Sie in die Trefferliste. GEO entscheidet, ob eine KI Sie in ihrer Antwort
                nennt — und das folgt anderen Regeln. Eine KI rankt keine zehn blauen Links, sondern erzeugt eine
                Antwort aus einigen wenigen Quellen, die sie für vertrauenswürdig hält. Wer dort nicht vorkommt, steht
                nicht auf Seite zwei. Er ist unsichtbar.
              </p>
              <p className="mb-5">
                Der Markt reagiert auf diese Verschiebung mit Versprechen, die fachlich nicht haltbar sind.
                Garantierte Platzierungen in KI-Antworten gibt es nicht: Welche Quellen ein System nennt, wechselt von
                Monat zu Monat und von Prompt zu Prompt. Wer Garantien verkauft, verkauft eine Illusion.
              </p>
              <p>
                Unser Standpunkt ist derselbe wie bei allem, was wir tun. GEO ist kein Selbstzweck. Nicht jede Branche
                und nicht jede Frage läuft über KI-Systeme — und wo sie es nicht tut, ist GEO verbranntes Budget. Wir
                prüfen zuerst, ob und wo KI-Sichtbarkeit für Ihr Geschäft überhaupt einen Hebel bildet, und investieren
                nur dort. Den Erfolg machen wir messbar: über Ihren Anteil an den KI-Antworten zu geschäftsrelevanten
                Fragen — nicht über eine Kennzahl, die gut aussieht und nichts bedeutet.
              </p>
            </div>
          </RevealOnScroll>
          <Callout>
            Sichtbarkeit in KI-Antworten ist nur dann wertvoll, wenn die Frage dahinter zu Ihrem Geschäft führt.
          </Callout>
        </Section>

        <Section
          variant="tinted"
          number="03"
          kicker="Vorgehen"
          headline={
            <>
              Vier Phasen, die KI-Sichtbarkeit <em>steuerbar</em> machen.
            </>
          }
        >
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-12 rounded-lg bg-white p-6 md:p-10">
              <div className="grid gap-8 md:grid-cols-[minmax(0,_2fr)_minmax(0,_3fr)] md:gap-14">
                <div>
                  <p className="eyebrow mb-3">Optionaler Baustein</p>
                  <h3 className="font-display text-xl text-ink">
                    Empfohlen werden heißt: anderswo genannt werden
                  </h3>
                </div>
                <p className="text-[17px] leading-relaxed text-ink-2">
                  LLMs ziehen ihre Antworten nicht nur aus Ihrer Website. Sie lernen aus dem, was Dritte über Sie
                  schreiben: Fachmedien, Branchenverzeichnisse, Vergleichsportale, Community-Plattformen. Ein
                  erheblicher Teil von GEO findet deshalb außerhalb Ihrer eigenen Seiten statt — konsistente
                  Entitäten-Signale im offenen Web, gezielte digitale PR und Präsenz dort, wo über Anbieter Ihrer
                  Kategorie gesprochen wird. Diese Arbeit ist langsamer als On-Page-Optimierung, aber sie entscheidet,
                  ob eine KI Sie von sich aus nennt.
                </p>
              </div>
            </div>
          </RevealOnScroll>
          <Callout>
            GEO ersetzt SEO nicht. Es baut auf derselben technischen Basis auf — und geht einen Schritt weiter.
          </Callout>
        </Section>

        <Section
          number="04"
          kicker="Capabilities & Stack"
          headline={
            <>
              Womit wir KI-Sichtbarkeit <em>steuerbar</em> machen.
            </>
          }
        >
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <RelatedServices
          locale="de"
          fromId="geo-generative-engine-optimization"
          number="06"
          items={relatedBySource["geo-generative-engine-optimization"]}
        />

        <Section number="07" kicker="FAQ" headline={<>Häufige Fragen zu <em>GEO</em>.</>}>
          <RevealOnScroll>
            <dl className="divide-y divide-rule rounded-lg border border-rule bg-white">
              {FAQS.map((f) => (
                <div key={f.q} className="px-6 py-6 md:px-8 md:py-7">
                  <dt className="font-display text-lg text-ink md:text-xl">{f.q}</dt>
                  <dd className="mt-3 text-[15px] leading-relaxed text-ink-3 md:text-base">{f.a}</dd>
                </div>
              ))}
            </dl>
          </RevealOnScroll>
        </Section>

        <Section kicker="Engagement">
          <CTABlock
            headline={
              <>
                Werden Sie in KI-Antworten <em>genannt</em> — wo es zählt?
              </>
            }
            body="Sie möchten wissen, ob und wo KI-Systeme Sie heute schon empfehlen — und welche Themen den Hebel bilden?"
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>

        <GlossaryTerms
          locale="de"
          ids={["geo", "ai-overviews", "ai-mode", "snippet-suitability", "eeat", "pillar-content"]}
        />
      </main>
      <Footer />
    </>
  );
}

